// src/modules/bookLocation/keyHandover/common/url/subscriptionQuery.ts

import { isValidId } from "./bookingQuerySchema";
import {
    SubscriptionQuery,
    SUBSCRIPTION_QUERY_KEYS,
    isValidSubscriptionFlow,
    isValidPaymentModel,
    isValidEmail,
    isValidBoolean,
} from "./subscriptionQuerySchema";

/**
 * Parse subscription-related query parameters from URL
 *
 * @param search - URL search string
 * @returns Validated SubscriptionQuery object
 */
export function parseSubscriptionQuery(search: string): SubscriptionQuery {
    const normalizedSearch = search.startsWith("?") ? search.substring(1) : search;
    const params = new URLSearchParams(normalizedSearch);
    const result: SubscriptionQuery = {};

    // Parse location ID
    const locationId = params.get("locationId");
    if (locationId && isValidId(locationId)) {
        result.locationId = locationId;
    }

    // Parse payment model
    const paymentModel = params.get("paymentModel");
    if (paymentModel && isValidPaymentModel(paymentModel)) {
        result.paymentModel = paymentModel as "subscription-new" | "subscription-existing" | "single-booking";
    }

    // Parse subscription flow
    const subscriptionFlow = params.get("subscriptionFlow");
    if (subscriptionFlow && isValidSubscriptionFlow(subscriptionFlow)) {
        result.subscriptionFlow = subscriptionFlow as "new" | "existing";
    }

    // Parse email
    const email = params.get("email");
    if (email && isValidEmail(email)) {
        result.email = email;
    }

    // Parse boolean flags
    const emailVerified = params.get("emailVerified");
    if (emailVerified && isValidBoolean(emailVerified)) {
        result.emailVerified = emailVerified as "true" | "false";
    }

    const eligible = params.get("eligible");
    if (eligible && isValidBoolean(eligible)) {
        result.eligible = eligible as "true" | "false";
    }

    const subscriptionPaid = params.get("subscriptionPaid");
    if (subscriptionPaid && isValidBoolean(subscriptionPaid)) {
        result.subscriptionPaid = subscriptionPaid as "true" | "false";
    }

    const subscriptionVerified = params.get("subscriptionVerified");
    if (subscriptionVerified && isValidBoolean(subscriptionVerified)) {
        result.subscriptionVerified = subscriptionVerified as "true" | "false";
    }

    // Parse selected subscription plan ID
    const selectedSubscriptionPlanId = params.get("selectedSubscriptionPlanId");
    if (selectedSubscriptionPlanId && isValidId(selectedSubscriptionPlanId)) {
        result.selectedSubscriptionPlanId = selectedSubscriptionPlanId;
    }

    // Parse subscription ID (completed subscription)
    const subscriptionId = params.get("subscriptionId");
    if (subscriptionId && isValidId(subscriptionId)) {
        result.subscriptionId = subscriptionId;
    }

    // Parse host data fields (simple string validation)
    const hostFirstName = params.get("hostFirstName");
    if (hostFirstName) result.hostFirstName = hostFirstName;

    const hostLastName = params.get("hostLastName");
    if (hostLastName) result.hostLastName = hostLastName;

    const hostEmail = params.get("hostEmail");
    if (hostEmail) result.hostEmail = hostEmail;

    const hostPhone = params.get("hostPhone");
    if (hostPhone) result.hostPhone = hostPhone;

    return result;
}

/**
 * Stringify subscription query into URL parameters
 *
 * @param query - SubscriptionQuery object
 * @returns URL query string (without leading '?')
 */
export function stringifySubscriptionQuery(query: SubscriptionQuery): string {
    const params = new URLSearchParams();

    SUBSCRIPTION_QUERY_KEYS.forEach((key) => {
        const value = query[key];
        if (value !== undefined && value !== null) {
            params.set(key, String(value));
        }
    });

    return params.toString();
}

/**
 * Merge booking and subscription queries
 *
 * @param bookingQuery - Booking query string or object
 * @param subscriptionQuery - Subscription query object
 * @returns Combined query string
 */
export function mergeQueries(bookingQuery: string, subscriptionQuery: SubscriptionQuery): string {
    const params = new URLSearchParams(
        bookingQuery.startsWith("?") ? bookingQuery.substring(1) : bookingQuery
    );

    SUBSCRIPTION_QUERY_KEYS.forEach((key) => {
        const value = subscriptionQuery[key];
        if (value !== undefined && value !== null) {
            params.set(key, String(value));
        }
    });

    return params.toString();
}
